import { delay } from 'redux-saga';
import { all, put, take, fork, select, race } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'react-router-redux';
import uuid from 'uuid/v4';

import {
  SET_MULTIPLES_TAB,
  GET_ACTIVE_MULTIPLES,
  getActiveMultiples,
  requestActiveMultiples,
  requestTotalOdds,
  CLEAR_MULTIPLE_SELECTIONS,
  ADD_SELECTIONS_TO_MULTIPLE,
  ADD_SELECTIONS_TO_MULTIPLE_WITH_CONFIG,
  addSelectionsToMultipleWithConfig,
} from '@xanadu/ui-shared/modules/multiples/actions';

import { LOGOUT_SUCCESS } from '@xanadu/ui-shared/modules/userSession/actions';
import { PAGE_VISIBILITY_CHANGE } from '@xanadu/ui-shared/modules/application/actions';
import { addUnsubmittedOffers } from '@xanadu/ui-shared/modules/unsubmittedOffers/actions';
import { requestMultipleEvents } from '@xanadu/ui-shared/modules/event/actions';
import { SET_EVENT_IDS_SUCCESS } from '@xanadu/ui-shared/modules/events/actions';

import { getNumberOfSelections } from '@xanadu/ui-shared/modules/multiples/selectors';

import { isNumberDecimalString } from '@xanadu/ui-shared/utils/numbers';

import { UPDATE_RIGHT_SIDEBAR, updateRightSidebar } from '../rightSidebar/actions';

import { sanitizeSearchParams } from './utils';

function* pollActiveMultiples() {
  let isActiveMultiples = false;
  while (true) {
    yield take(GET_ACTIVE_MULTIPLES);
    isActiveMultiples = true;

    while (isActiveMultiples) {
      yield put(requestActiveMultiples());

      const { tabChange, sidebarChange, logout } = yield race({
        task: delay(10000),
        tabChange: take(SET_MULTIPLES_TAB),
        sidebarChange: take(UPDATE_RIGHT_SIDEBAR),
        logout: take(LOGOUT_SUCCESS)
      });

      if (tabChange || sidebarChange || logout) {
        isActiveMultiples = false;
      } else {
        const visible = yield select(state => state.getIn(['application', 'visible']));

        if (visible) {
          isActiveMultiples = true;
        } else {
          // eslint-disable-next-line no-console
          console.info('Active multiples queued');
          yield take(PAGE_VISIBILITY_CHANGE);
          yield put(getActiveMultiples());
        }
      }
    }
  }
}

function* pollTotalOddsToValidateSelections() {
  let isBuildingMultiple = false;

  while (true) {
    const { sidebar } = yield race({
      sidebar: take(UPDATE_RIGHT_SIDEBAR),
      addSelection: take([ADD_SELECTIONS_TO_MULTIPLE, ADD_SELECTIONS_TO_MULTIPLE_WITH_CONFIG]),
    });

    const content = sidebar ? sidebar.payload.content : 'multiples';
    let selections = yield select(getNumberOfSelections);

    if (content === 'multiples' && selections > 1) {
      isBuildingMultiple = true;

      while (isBuildingMultiple) {
        yield put(requestTotalOdds({ noLoading: true }));

        const {
          sidebarChanged,
          clearSelections,
          logout,
        } = yield race({
          task: delay(5000),
          sidebarChanged: take(UPDATE_RIGHT_SIDEBAR),
          clearSelections: take(CLEAR_MULTIPLE_SELECTIONS),
          logout: take(LOGOUT_SUCCESS)
        });

        selections = yield select(getNumberOfSelections);

        if (sidebarChanged
          && sidebarChanged.payload.content !== 'multiples'
          || selections < 2
          || clearSelections
          || logout
        ) {
          isBuildingMultiple = false;
        } else {
          const visible = yield select(state => state.getIn(['application', 'visible']));

          if (visible) {
            isBuildingMultiple = true;
          } else {
            // eslint-disable-next-line no-console
            console.info('Total odds request queued');
            yield take(PAGE_VISIBILITY_CHANGE);

            yield put(requestTotalOdds({ noLoading: true }));
          }
        }
      }
    }
  }
}

/* The purpose of this saga is to allow external services to call
  our UI and populate dynamically the betslip passing all the proper informations
 */
function* watchForNewMultipleDynamicOffer() {
  while (true) {
    yield take(LOCATION_CHANGE);
    const route = yield select(state => state.getIn(['routing', 'location']));

    const search = route.get('search');

    const { stake, permutationType, multipleBet } = sanitizeSearchParams(search);

    if (multipleBet) {
      const eventIds = multipleBet.map(({ eventId }) => eventId);

      if (eventIds.length) {
        yield put(requestMultipleEvents({
          params: {
            ids: eventIds
          }
        }));

        const { payload: eventsStatus } = yield take(SET_EVENT_IDS_SUCCESS);

        if (eventsStatus) {
          const unsubmittedMultipleOffers = [];

          for (let index = 0; index < multipleBet.length; index++) {
            const { eventId, runnerId } = multipleBet[index];
            const event = yield select(state => state.getIn(['event', eventId], false));

            if (event) {
              const runner = yield select(state => state.getIn(['runner', runnerId], false));
              if (runner) {
                const market = yield select(state => state.getIn(['market', runner.marketId], false));
                if (market) {
                  const prices = yield select(state => state.getIn(['price', runner.id], []));
                  const price = prices.find(({ side }) => side === 'back', false);
                  const { oddsType, side = 'back', odds } = price || {};
                  const generatedUUIDV4 = uuid();
                  const now = Date.now();
                  unsubmittedMultipleOffers.push({
                    odds,
                    side,
                    oddsType,
                    eventId,
                    // NOTE: Because uniqueId is only unique to this pageload and gets persisted by Eddie
                    cid: `${generatedUUIDV4}.${now}`,
                    runnerId: runner.id,
                    marketId: runner.marketId,
                    marketType: market.marketType,
                    clientOfferId: `mb-web-${generatedUUIDV4}-${now}`,
                  });
                }
              }
            }
          }

          if (unsubmittedMultipleOffers.length) {
            // trigger the multiples sidebar menu to open
            yield put(updateRightSidebar({
              visible: true,
              content: 'multiples'
            }));
            // add event to singles bet view
            yield put(addUnsubmittedOffers(unsubmittedMultipleOffers));
            // add event to multiples bet view
            yield put(
              addSelectionsToMultipleWithConfig({
                selections: unsubmittedMultipleOffers,
                stake: stake && isNumberDecimalString(stake) ? stake : undefined,
                permutationType: permutationType ? permutationType.toUpperCase() : undefined,
              })
            );
          }
        }
      }
    }
  }
}

export default function* root() {
  yield all([
    fork(pollActiveMultiples),
    fork(watchForNewMultipleDynamicOffer),
    fork(pollTotalOddsToValidateSelections),
  ]);
}
